/**
 * Mock Zerovia Dataset。
 *
 * - 決定的（乱数・現在時刻に依存しない）。Test/Browserで同じ結果を再現できる。
 * - Secret本文、API Key、Cookie、Token本文を含めない。`secretRef`だけを持つ。
 * - Tenant分離を検証できるよう、必ず2 Tenant以上を持つ。
 */
import type {
	Agent,
	AgentSession,
	Artifact,
	ChatMessage,
	NodeType,
	Project,
	Tenant,
	TenantMembership,
	Ucs,
	User,
	VerticalPackage,
	WorkflowDefinition,
	WorkflowExecution,
	WorkflowNodeDefinition,
	WorkspaceTypeDefinition,
} from "@agentry/zerovia-contracts";
import type {
	ApprovalRequest,
	Budget,
	ByoPolicy,
	CatalogPublication,
	ContactMessage,
	Integration,
	Notice,
	NotificationItem,
	OwnershipTransferRecord,
	PersonalCredential,
	Plan,
	PoolAssignment,
	ProductSettings,
	ProviderUsageRule,
	RoleDefinition,
	StorageObject,
	TenantCatalogEntitlement,
	TenantInvite,
	TenantMember,
	TenantSettings,
	TenantSummary,
	WorkspaceEntry,
} from "../agentry-types.js";
import type { AuditEventView, CostEventView, ProviderView } from "../types.js";

export const T0 = "2026-08-20T09:00:00.000Z";
const t = (minutes: number) => new Date(Date.parse(T0) + minutes * 60_000).toISOString();

export const TENANT_MAIN = "tenant-01";
export const TENANT_OTHER = "tenant-02";
export const PROJECT_MAIN = "project-01";
export const PROJECT_SECOND = "project-02";
export const PROJECT_OTHER = "project-90";
export const REPRESENTATIVE_UCS = "UCS-WEB-010-01-v1";

/** Mockログイン用のCredential。Password本文はDemo用固定値でSecretではない。 */
export type MockAccount = { email: string; password: string; userId: string };

export const MOCK_ACCOUNTS: MockAccount[] = [
	{ email: "user@example.test", password: "password123", userId: "user-01" },
	{ email: "admin@example.test", password: "password123", userId: "user-02" },
	{ email: "system@agentry.test", password: "password123", userId: "user-03" },
	{ email: "other@other.test", password: "password123", userId: "user-90" },
];

export const tenants: Tenant[] = [
	{
		id: TENANT_MAIN,
		name: "Aoyama Beauty Clinic Group",
		slug: "aoyama-beauty",
		status: "ACTIVE",
		createdAt: t(0),
	},
	{
		id: TENANT_OTHER,
		name: "Sakura Dining Holdings",
		slug: "sakura-dining",
		status: "ACTIVE",
		createdAt: t(0),
	},
];

export const users: User[] = [
	{
		id: "user-01",
		email: "user@example.test",
		name: "田中 彩",
		status: "ACTIVE",
		createdAt: t(0),
	},
	{
		id: "user-02",
		email: "admin@example.test",
		name: "佐藤 健",
		status: "ACTIVE",
		createdAt: t(0),
	},
	{
		id: "user-03",
		email: "system@agentry.test",
		name: "Agentry Product Admin",
		status: "ACTIVE",
		createdAt: t(0),
	},
	{
		id: "user-04",
		email: "member2@example.test",
		name: "鈴木 涼",
		status: "ACTIVE",
		createdAt: t(0),
	},
	{
		id: "user-90",
		email: "other@other.test",
		name: "別Tenant User",
		status: "ACTIVE",
		createdAt: t(0),
	},
];

export const memberships: TenantMembership[] = [
	{
		id: "membership-01",
		userId: "user-01",
		tenantId: TENANT_MAIN,
		role: "tenant_member",
		status: "ACTIVE",
		createdAt: t(0),
	},
	{
		id: "membership-02",
		userId: "user-02",
		tenantId: TENANT_MAIN,
		role: "tenant_admin",
		status: "ACTIVE",
		createdAt: t(0),
	},
	{
		id: "membership-03",
		userId: "user-03",
		tenantId: TENANT_MAIN,
		role: "platform_admin",
		status: "ACTIVE",
		createdAt: t(0),
	},
	{
		id: "membership-04",
		userId: "user-04",
		tenantId: TENANT_MAIN,
		role: "tenant_member",
		status: "ACTIVE",
		createdAt: t(0),
	},
	{
		id: "membership-90",
		userId: "user-90",
		tenantId: TENANT_OTHER,
		role: "tenant_member",
		status: "ACTIVE",
		createdAt: t(0),
	},
];

export const projects: Project[] = [
	{
		id: PROJECT_MAIN,
		tenantId: TENANT_MAIN,
		name: "青山院 秋季キャンペーン",
		description: "医療脱毛の秋季新規獲得キャンペーンLP",
		ownerUserId: "user-01",
		status: "ACTIVE",
		workspaceType: "lp",
		verticalKey: "workspace.lp",
		defaultUcsId: "UCS-WS-LP-v1",
		createdAt: t(0),
	},
	{
		id: PROJECT_SECOND,
		tenantId: TENANT_MAIN,
		name: "銀座院 サイトリニューアル",
		description: "既存サイトの導線改善と予約Conversion改善",
		ownerUserId: "user-04",
		status: "ACTIVE",
		workspaceType: "app",
		verticalKey: "workspace.app",
		defaultUcsId: "UCS-WS-APP-v1",
		createdAt: t(0),
	},
	{
		id: PROJECT_OTHER,
		tenantId: TENANT_OTHER,
		name: "他Tenant Project",
		description: "Tenant分離検証用",
		ownerUserId: "user-90",
		status: "ACTIVE",
		workspaceType: "generic",
		verticalKey: "workspace.generic",
		defaultUcsId: "UCS-WS-GENERIC-v1",
		createdAt: t(0),
	},
];

/**
 * Mock mirror of Zerovia workspace-type Vertical templates.
 * UI must load these via listWorkspaceTypes — do not hardcode as Agentry production canon.
 */
export const workspaceTypeDefinitions: WorkspaceTypeDefinition[] = [
	{
		type: "generic",
		displayName: "汎用",
		description: "自分のみで進める汎用ワークスペース。",
		verticalKey: "workspace.generic",
		defaultUcsId: "UCS-WS-GENERIC-v1",
		roles: [{ key: "owner", displayName: "自分", kind: "human", agentKey: null }],
	},
	{
		type: "lp",
		displayName: "LP作成",
		description: "マネージャーが LP / デザイン Agent に依頼して LP を作る。",
		verticalKey: "workspace.lp",
		defaultUcsId: "UCS-WS-LP-v1",
		roles: [
			{ key: "owner", displayName: "自分", kind: "human", agentKey: null },
			{
				key: "manager",
				displayName: "マネージャー",
				kind: "agent",
				agentKey: "workspace.lp.manager",
			},
			{ key: "lp", displayName: "LP Agent", kind: "agent", agentKey: "workspace.lp.builder" },
			{
				key: "design",
				displayName: "デザイン Agent",
				kind: "agent",
				agentKey: "workspace.lp.design",
			},
		],
	},
	{
		type: "app",
		displayName: "App作成",
		description: "マネージャーがインフラ / 設計 / コーディング Agent に依頼して App を作る。",
		verticalKey: "workspace.app",
		defaultUcsId: "UCS-WS-APP-v1",
		roles: [
			{ key: "owner", displayName: "自分", kind: "human", agentKey: null },
			{
				key: "manager",
				displayName: "マネージャー",
				kind: "agent",
				agentKey: "workspace.app.manager",
			},
			{
				key: "infra",
				displayName: "インフラ Agent",
				kind: "agent",
				agentKey: "workspace.app.infra",
			},
			{
				key: "design",
				displayName: "設計 Agent",
				kind: "agent",
				agentKey: "workspace.app.design",
			},
			{
				key: "coding",
				displayName: "コーディング Agent",
				kind: "agent",
				agentKey: "workspace.app.coding",
			},
		],
	},
	{
		type: "business",
		displayName: "事業立案",
		description: "マネージャーと事業立案 Agent で企画を進める。",
		verticalKey: "workspace.business",
		defaultUcsId: "UCS-WS-BUSINESS-v1",
		roles: [
			{ key: "owner", displayName: "自分", kind: "human", agentKey: null },
			{
				key: "manager",
				displayName: "マネージャー",
				kind: "agent",
				agentKey: "workspace.business.manager",
			},
			{
				key: "planner",
				displayName: "事業立案 Agent",
				kind: "agent",
				agentKey: "workspace.business.planner",
			},
		],
	},
];

export const repositories = [
	{
		id: "repository-zerovia",
		name: "Zerovia",
		cloneUrl: "https://git.example.test/zerovia/zerovia.git",
		defaultBranch: "main",
		mirrorStatus: "SYNCED" as const,
	},
	{
		id: "repository-agentry",
		name: "Agentry",
		cloneUrl: "https://git.example.test/agentry/agentry.git",
		defaultBranch: "main",
		mirrorStatus: "SYNCED" as const,
	},
];

export const projectRepositoryConnections = [
	{
		projectId: PROJECT_MAIN,
		repositoryId: "repository-agentry",
		repositoryName: "Agentry",
		provider: "AGENTRY" as const,
		connectedAt: t(0),
	},
];

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export const agents: Agent[] = [
	{
		id: "agent-common-lp",
		key: "common.landing-page",
		displayName: "LP制作 共通Agent",
		layer: "COMMON",
		parentAgentId: null,
		verticalKey: null,
		description: "Discovery→Research→構成→Copy→Design→実装→QAの標準工程を担当する。",
		defaultTaskType: "PLANNING",
		status: "PUBLISHED",
		version: 3,
		createdAt: t(0),
	},
	{
		id: "agent-vertical-beauty",
		key: "beauty.landing-page",
		displayName: "美容クリニックLP Agent",
		layer: "VERTICAL",
		parentAgentId: "agent-common-lp",
		verticalKey: "beauty_clinic",
		description: "医療脱毛・症例・医療広告表現・予約導線に対応した美容クリニック特化Agent。",
		defaultTaskType: "PLANNING",
		status: "PUBLISHED",
		version: 5,
		createdAt: t(0),
	},
	{
		id: "agent-vertical-restaurant",
		key: "restaurant.campaign",
		displayName: "飲食店Campaign Agent",
		layer: "VERTICAL",
		parentAgentId: "agent-common-lp",
		verticalKey: "restaurant",
		description: "予約・来店Conversionを目的としたCampaign設計とLP制作を担当する。",
		defaultTaskType: "PLANNING",
		status: "PUBLISHED",
		version: 2,
		createdAt: t(0),
	},
	{
		id: "agent-common-research",
		key: "common.research",
		displayName: "汎用Research Agent",
		layer: "COMMON",
		parentAgentId: null,
		verticalKey: null,
		description: "市場・競合・法規制の調査とEvidence付きレポート生成を行う。",
		defaultTaskType: "RESEARCH",
		status: "PUBLISHED",
		version: 4,
		createdAt: t(0),
	},
	{
		id: "agent-custom-aoyama",
		key: "custom.aoyama-brand",
		displayName: "青山院 Brand Custom Agent",
		layer: "CUSTOM",
		parentAgentId: "agent-vertical-beauty",
		verticalKey: "beauty_clinic",
		description: "自院のBrand Tone、禁止表現、予約APIに合わせて出力を調整する。",
		defaultTaskType: "DESIGN",
		status: "DRAFT",
		version: 1,
		createdAt: t(0),
	},
];

export const ucsList: Ucs[] = [
	{
		id: REPRESENTATIVE_UCS,
		displayName: "美容クリニックLP制作",
		categoryCode: "WEB",
		description:
			"Guided Interviewから構成・Copy・Design・Quality Gate・承認・成果物確定までを一気通貫で行う代表UCS。",
		agentId: "agent-vertical-beauty",
		verticalKey: "beauty_clinic",
		workflowDefinitionKey: "wf.beauty.landing-page",
		status: "PUBLISHED",
		createdAt: t(0),
	},
	{
		id: "UCS-WEB-001-02-v1",
		displayName: "LP制作 汎用",
		categoryCode: "WEB",
		description: "業種を限定しない汎用LP制作。Vertical Knowledgeを持たない標準工程。",
		agentId: "agent-common-lp",
		verticalKey: null,
		workflowDefinitionKey: "wf.common.landing-page",
		status: "PUBLISHED",
		createdAt: t(0),
	},
	{
		id: "UCS-CMN-002-01-v1",
		displayName: "汎用Research",
		categoryCode: "CMN",
		description: "市場・競合・規制の調査とEvidence付きレポート生成。",
		agentId: "agent-common-research",
		verticalKey: null,
		workflowDefinitionKey: "wf.common.research",
		status: "PUBLISHED",
		createdAt: t(0),
	},
	{
		id: "UCS-WEB-020-01-v1",
		displayName: "飲食店Campaign LP",
		categoryCode: "WEB",
		description: "季節Campaignの予約獲得を目的とした飲食店向けLP制作。",
		agentId: "agent-vertical-restaurant",
		verticalKey: "restaurant",
		workflowDefinitionKey: "wf.restaurant.campaign",
		status: "PUBLISHED",
		createdAt: t(0),
	},
	{
		id: "UCS-MKT-001-01-v1",
		displayName: "運用改善 / CRO",
		categoryCode: "MKT",
		description: "公開後のConversion計測とA/B改善提案。",
		agentId: "agent-common-lp",
		verticalKey: null,
		workflowDefinitionKey: "wf.common.cro",
		status: "DRAFT",
		createdAt: t(0),
	},
];

export const verticalPackages: VerticalPackage[] = [
	{
		id: "vp-beauty-01",
		key: "beauty_clinic",
		displayName: "美容クリニック Vertical Package",
		version: 5,
		industryKnowledge: [
			{
				title: "医療広告ガイドライン",
				body: "術前術後写真は必要な説明を併記する。誇大表現・体験談の広告利用は不可。",
			},
			{
				title: "初回価格表記",
				body: "適用条件、回数、税込表記、期間を明示する。総額が分かる表記を必須とする。",
			},
			{ title: "予約導線", body: "Web予約、LINE、電話をMobile画面上部から到達可能にする。" },
		],
		guidedQuestions: [
			{
				key: "goal",
				prompt: "今回のLPで最も増やしたい成果はどれですか。",
				inputType: "SINGLE_SELECT",
				options: ["Web予約", "LINE友だち追加", "電話問い合わせ", "資料請求"],
				required: true,
				helpText: "最優先のConversionを1つ選びます。後の構成とQuality Gateがこれに従います。",
			},
			{
				key: "menu",
				prompt: "訴求する主要メニューと価格を教えてください。",
				inputType: "LONG_TEXT",
				options: [],
				required: true,
				helpText: "税込価格、回数、適用条件を含めてください。",
			},
			{
				key: "target",
				prompt: "主なTarget像に近いものを選んでください。",
				inputType: "MULTI_SELECT",
				options: ["20代女性", "30代女性", "40代以上", "男性", "学生", "初回来院"],
				required: true,
				helpText: null,
			},
			{
				key: "area",
				prompt: "商圏と競合クリニックを教えてください。",
				inputType: "TEXT",
				options: [],
				required: true,
				helpText: "最寄駅、徒歩分数、比較されやすい競合名。",
			},
			{
				key: "assets",
				prompt: "利用できる素材はどれですか。",
				inputType: "MULTI_SELECT",
				options: ["症例写真", "院内写真", "医師写真", "ロゴ", "動画", "口コミ"],
				required: false,
				helpText: "症例写真は掲載条件のQuality Gateが追加されます。",
			},
			{
				key: "budget",
				prompt: "Campaign期間と目標予約数を教えてください。",
				inputType: "TEXT",
				options: [],
				required: false,
				helpText: null,
			},
		],
		workflowExtensions: [
			{ nodeKey: "medical-ad-compliance-gate", operation: "ADD" },
			{ nodeKey: "case-photo-consent-gate", operation: "ADD" },
			{ nodeKey: "structure", operation: "CONFIGURE" },
		],
		templates: [
			{
				key: "lp-structure-beauty",
				mimeType: "text/markdown",
				body: "# 美容クリニックLP標準構成\n",
			},
			{ key: "faq-beauty", mimeType: "text/markdown", body: "# よくある質問テンプレート\n" },
		],
		qualityGates: [
			{
				key: "medical-ad-compliance",
				displayName: "医療広告表現チェック",
				gateType: "POLICY",
				evaluator: "LLM_JUDGE",
				threshold: 0.95,
				strengthensGateKey: "content-policy",
			},
			{
				key: "price-accuracy",
				displayName: "価格・適用条件の正確性",
				gateType: "QUALITY",
				evaluator: "BOOLEAN",
				threshold: null,
				strengthensGateKey: null,
			},
			{
				key: "reservation-path",
				displayName: "Mobile予約導線の到達性",
				gateType: "QUALITY",
				evaluator: "AUTOMATED_TEST",
				threshold: null,
				strengthensGateKey: null,
			},
		],
		kpis: [
			{ key: "reservation_cvr", displayName: "予約CVR", unit: "%" },
			{ key: "cost_per_reservation", displayName: "予約単価", unit: "JPY" },
			{ key: "line_add_rate", displayName: "LINE登録率", unit: "%" },
		],
		budget: { improve_total_minutes: 60 },
		status: "PUBLISHED",
		contentHash: "sha256:beauty-vp-v5",
		createdAt: t(0),
	},
	{
		id: "vp-restaurant-01",
		key: "restaurant",
		displayName: "飲食 Vertical Package",
		version: 2,
		industryKnowledge: [
			{ title: "予約条件", body: "コース人数、当日受付、Cancel Policy、No-show対応を明示する。" },
			{ title: "Allergen表記", body: "根拠と最終確認導線を併記し、断定表現を避ける。" },
		],
		guidedQuestions: [
			{
				key: "goal",
				prompt: "最優先のConversionはどれですか。",
				inputType: "SINGLE_SELECT",
				options: ["Web予約", "電話予約", "来店", "宴会問い合わせ"],
				required: true,
				helpText: null,
			},
			{
				key: "course",
				prompt: "重点コースと価格を教えてください。",
				inputType: "LONG_TEXT",
				options: [],
				required: true,
				helpText: null,
			},
		],
		workflowExtensions: [{ nodeKey: "menu-accuracy-gate", operation: "ADD" }],
		templates: [
			{ key: "lp-structure-restaurant", mimeType: "text/markdown", body: "# 飲食店LP標準構成\n" },
		],
		qualityGates: [
			{
				key: "menu-accuracy",
				displayName: "営業時間・価格・Menu整合",
				gateType: "QUALITY",
				evaluator: "BOOLEAN",
				threshold: null,
				strengthensGateKey: null,
			},
		],
		kpis: [{ key: "reservation_cvr", displayName: "予約CVR", unit: "%" }],
		budget: { improve_total_minutes: 60 },
		status: "PUBLISHED",
		contentHash: "sha256:restaurant-vp-v2",
		createdAt: t(0),
	},
];

type LegacyWorkflowNode = { id: string; type: string; name: string; required: boolean };

const NODE_TYPES: Record<string, NodeType> = {
	Start: "START",
	AgentInterview: "AGENT_INTERVIEW",
	AgentTask: "AGENT_TASK",
	QualityGate: "QUALITY_GATE",
	UserApproval: "APPROVAL",
	Artifact: "ARTIFACT",
	End: "END",
};

function workflowNodes(nodes: LegacyWorkflowNode[]): WorkflowNodeDefinition[] {
	return nodes.map((node, index) => {
		const nextNode = nodes[index + 1];
		return {
			nodeId: node.id,
			nodeType: NODE_TYPES[node.type] ?? "AGENT_TASK",
			stageId: "main",
			displayName: node.name,
			next: nextNode ? [nextNode.id] : [],
			taskProfile: null,
			promptTemplate: null,
			artifactType: node.type === "Artifact" ? "DOCUMENT" : null,
			artifactMimeType: node.type === "Artifact" ? "text/markdown" : null,
			artifactSourceVariable: node.type === "Artifact" ? "content" : null,
			gateType:
				node.type === "UserApproval" ? "APPROVAL" : node.type === "QualityGate" ? "QUALITY" : null,
			outputVariable: null,
			required: node.required,
			retryPolicy: {
				maxAttempts: 3,
				initialDelayMs: 50,
				backoffMultiplier: 2,
				maxDelayMs: 2000,
				retryableErrorClasses: ["TRANSIENT_PROVIDER", "TRANSIENT_RUNTIME"],
			},
			attemptTimeoutMs: 60_000,
			effectName: node.type === "Artifact" ? "artifact.commit" : null,
			isReversible: true,
			regulationCheck: null,
			abMeasurement: null,
			proposalGeneration: null,
			researchGeneration: null,
			improvementGeneration: null,
			modality: null,
			modalityOptional: false,
			modalityPostconditions: [],
		};
	});
}

const beautyNodes: LegacyWorkflowNode[] = [
	{ id: "start", type: "Start", name: "開始", required: true },
	{ id: "interview", type: "AgentInterview", name: "Guided Interview", required: true },
	{ id: "research", type: "AgentTask", name: "商圏・競合Research", required: true },
	{ id: "requirement-gate", type: "QualityGate", name: "要件Gate", required: true },
	{ id: "structure", type: "AgentTask", name: "構成設計", required: true },
	{ id: "copy", type: "AgentTask", name: "Copy作成", required: true },
	{
		id: "medical-ad-compliance-gate",
		type: "QualityGate",
		name: "医療広告表現Gate",
		required: true,
	},
	{ id: "design", type: "AgentTask", name: "Design案生成", required: true },
	{ id: "approval", type: "UserApproval", name: "ユーザー承認", required: true },
	{ id: "artifact", type: "Artifact", name: "成果物確定", required: true },
	{ id: "end", type: "End", name: "完了", required: true },
];

export const workflowDefinitions: WorkflowDefinition[] = [
	{
		id: "wfdef-beauty-lp",
		key: "wf.beauty.landing-page",
		displayName: "美容クリニックLP制作Workflow",
		version: 4,
		status: "PUBLISHED",
		stages: [{ stageId: "main", displayName: "実行", sequence: 0 }],
		nodes: workflowNodes(beautyNodes),
		workflowDeadlineMs: 3_600_000,
		definitionHash: "sha256:wf-beauty-v4",
		createdAt: t(0),
		publishedAt: t(0),
	},
	{
		id: "wfdef-common-lp",
		key: "wf.common.landing-page",
		displayName: "汎用LP制作Workflow",
		version: 6,
		status: "PUBLISHED",
		stages: [{ stageId: "main", displayName: "実行", sequence: 0 }],
		nodes: workflowNodes(beautyNodes.filter((n) => n.id !== "medical-ad-compliance-gate")),
		workflowDeadlineMs: 3_600_000,
		definitionHash: "sha256:wf-common-lp-v6",
		createdAt: t(0),
		publishedAt: t(0),
	},
	{
		id: "wfdef-common-research",
		key: "wf.common.research",
		displayName: "汎用ResearchWorkflow",
		version: 2,
		status: "PUBLISHED",
		stages: [{ stageId: "main", displayName: "実行", sequence: 0 }],
		nodes: workflowNodes([
			{ id: "start", type: "Start", name: "開始", required: true },
			{ id: "interview", type: "AgentInterview", name: "調査要件Interview", required: true },
			{ id: "research", type: "AgentTask", name: "調査実行", required: true },
			{ id: "evidence-gate", type: "QualityGate", name: "Evidence Gate", required: true },
			{ id: "artifact", type: "Artifact", name: "Report確定", required: true },
			{ id: "end", type: "End", name: "完了", required: true },
		]),
		workflowDeadlineMs: 3_600_000,
		definitionHash: "sha256:wf-research-v2",
		createdAt: t(0),
		publishedAt: t(0),
	},
	{
		id: "wfdef-restaurant",
		key: "wf.restaurant.campaign",
		displayName: "飲食店CampaignWorkflow",
		version: 1,
		status: "PUBLISHED",
		stages: [{ stageId: "main", displayName: "実行", sequence: 0 }],
		nodes: workflowNodes(
			beautyNodes.map((n) =>
				n.id === "medical-ad-compliance-gate"
					? { id: "menu-accuracy-gate", type: "QualityGate", name: "Menu整合Gate", required: true }
					: n,
			),
		),
		workflowDeadlineMs: 3_600_000,
		definitionHash: "sha256:wf-restaurant-v1",
		createdAt: t(0),
		publishedAt: t(0),
	},
];

// ---------------------------------------------------------------------------
// Session / Execution / Chat
// ---------------------------------------------------------------------------

export const sessions: AgentSession[] = [
	{
		id: "session-01",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		ownerUserId: "user-01",
		ucsId: REPRESENTATIVE_UCS,
		agentId: "agent-vertical-beauty",
		businessPurpose: "秋季医療脱毛キャンペーンの新規予約を増やす",
		sessionMode: "GUIDED_WORKFLOW",
		title: "秋季キャンペーンLP制作",
		status: "ACTIVE",
		securityClassification: "INTERNAL",
		createdBy: "user-01",
		createdAt: t(10),
		updatedAt: t(64),
	},
	{
		id: "session-02",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_SECOND,
		ownerUserId: "user-04",
		ucsId: "UCS-CMN-002-01-v1",
		agentId: "agent-common-research",
		businessPurpose: "競合クリニックの価格改定を追跡する",
		sessionMode: "FREE_CHAT",
		title: "競合価格Research",
		status: "ACTIVE",
		securityClassification: "INTERNAL",
		createdBy: "user-04",
		createdAt: t(20),
		updatedAt: t(40),
	},
	{
		id: "session-03",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		ownerUserId: "user-01",
		ucsId: "UCS-WEB-001-02-v1",
		agentId: "agent-common-lp",
		businessPurpose: "銀座院サイトの導線改善案",
		sessionMode: "GUIDED_WORKFLOW",
		title: "銀座院 導線改善",
		status: "ARCHIVED",
		securityClassification: "INTERNAL",
		createdBy: "user-01",
		createdAt: t(5),
		updatedAt: t(30),
	},
	{
		id: "session-90",
		tenantId: TENANT_OTHER,
		projectId: PROJECT_OTHER,
		ownerUserId: "user-90",
		ucsId: "UCS-WEB-020-01-v1",
		agentId: "agent-vertical-restaurant",
		businessPurpose: "他Tenantの秋Campaign",
		sessionMode: "GUIDED_WORKFLOW",
		title: "他Tenant Session",
		status: "ACTIVE",
		securityClassification: "INTERNAL",
		createdBy: "user-90",
		createdAt: t(12),
		updatedAt: t(12),
	},
];

export const messages: ChatMessage[] = [
	{
		id: "msg-01",
		sessionId: "session-01",
		tenantId: TENANT_MAIN,
		sequence: 0,
		role: "AGENT",
		content:
			"美容クリニックLP制作を担当します。まず今回のLPで最も増やしたい成果を確認させてください。",
		providerInvocationId: null,
		routingDecisionId: null,
		executionId: null,
		createdAt: t(10),
	},
	{
		id: "msg-02",
		sessionId: "session-01",
		tenantId: TENANT_MAIN,
		sequence: 1,
		role: "USER",
		content: "Web予約を増やしたいです。",
		providerInvocationId: null,
		routingDecisionId: null,
		executionId: null,
		createdAt: t(12),
	},
	{
		id: "msg-03",
		sessionId: "session-01",
		tenantId: TENANT_MAIN,
		sequence: 2,
		role: "AGENT",
		content:
			"Web予約を最優先のConversionとして設定しました。次に訴求する主要メニューと価格を教えてください。税込・回数・適用条件を含めてください。",
		providerInvocationId: "pinv-01",
		routingDecisionId: "rdec-01",
		executionId: null,
		createdAt: t(13),
	},
];

type LegacyExecution = {
	id: string;
	tenantId: string;
	projectId: string;
	sessionId: string;
	workflowDefinitionId: string;
	workflowVersion: number;
	executionSnapshotHash: string;
	policyHash: string;
	status: WorkflowExecution["status"];
	waitReason: WorkflowExecution["waitReason"];
	failureClass: WorkflowExecution["failureClass"];
	idempotencyKey: string;
	stateVersion: number;
	currentCheckpointId: string | null;
	createdAt: string;
	updatedAt: string;
	endedAt: string | null;
};

function mockExecution(input: LegacyExecution): WorkflowExecution {
	const session = sessions.find((candidate) => candidate.id === input.sessionId);
	if (!session) throw new Error(`Session ${input.sessionId} not found`);
	return {
		id: input.id,
		tenantId: input.tenantId,
		projectId: input.projectId,
		sessionId: input.sessionId,
		ucsId: session.ucsId,
		workflowDefinitionId: input.workflowDefinitionId,
		workflowVersion: input.workflowVersion,
		snapshotHash: input.executionSnapshotHash,
		effectiveWorkflowHash: null,
		effectiveWorkflowProvenance: null,
		rootExecutionId: input.id,
		parentExecutionId: null,
		resumeFromCheckpointId: null,
		replayOfExecutionId: null,
		status: input.status,
		waitReason: input.waitReason,
		failureClass: input.failureClass,
		failureDetail: null,
		triggerType: "USER",
		idempotencyKey: input.idempotencyKey,
		stateVersion: input.stateVersion,
		startedByUserId: session.createdBy,
		ownerUserId: session.ownerUserId,
		startedAt: input.createdAt,
		deadlineAt: null,
		endedAt: input.endedAt,
		currentCheckpointId: input.currentCheckpointId,
		estimatedCost: 0,
		committedCost: 0,
		currency: "JPY",
		createdAt: input.createdAt,
		updatedAt: input.updatedAt,
	};
}

export const executions: WorkflowExecution[] = [
	mockExecution({
		id: "execution-01",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		sessionId: "session-01",
		workflowDefinitionId: "wfdef-beauty-lp",
		workflowVersion: 4,
		executionSnapshotHash: "sha256:exec-snap-01",
		policyHash: "sha256:policy-01",
		status: "WAITING",
		waitReason: "USER_APPROVAL",
		failureClass: null,
		idempotencyKey: "chat-poc-001",
		stateVersion: 7,
		currentCheckpointId: "checkpoint-03",
		createdAt: t(14),
		updatedAt: t(64),
		endedAt: null,
	}),
	mockExecution({
		id: "execution-02",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_SECOND,
		sessionId: "session-02",
		workflowDefinitionId: "wfdef-common-research",
		workflowVersion: 2,
		executionSnapshotHash: "sha256:exec-snap-02",
		policyHash: "sha256:policy-01",
		status: "SUCCEEDED",
		waitReason: null,
		failureClass: null,
		idempotencyKey: "chat-poc-002",
		stateVersion: 11,
		currentCheckpointId: "checkpoint-09",
		createdAt: t(20),
		updatedAt: t(41),
		endedAt: t(41),
	}),
	mockExecution({
		id: "execution-03",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		sessionId: "session-03",
		workflowDefinitionId: "wfdef-common-lp",
		workflowVersion: 6,
		executionSnapshotHash: "sha256:exec-snap-03",
		policyHash: "sha256:policy-01",
		status: "FAILED",
		waitReason: null,
		failureClass: "TRANSIENT_PROVIDER",
		idempotencyKey: "chat-poc-003",
		stateVersion: 5,
		currentCheckpointId: "checkpoint-05",
		createdAt: t(6),
		updatedAt: t(31),
		endedAt: t(31),
	}),
	mockExecution({
		id: "execution-90",
		tenantId: TENANT_OTHER,
		projectId: PROJECT_OTHER,
		sessionId: "session-90",
		workflowDefinitionId: "wfdef-restaurant",
		workflowVersion: 1,
		executionSnapshotHash: "sha256:exec-snap-90",
		policyHash: "sha256:policy-01",
		status: "RUNNING",
		waitReason: null,
		failureClass: null,
		idempotencyKey: "chat-poc-090",
		stateVersion: 3,
		currentCheckpointId: null,
		createdAt: t(12),
		updatedAt: t(15),
		endedAt: null,
	}),
];

// ---------------------------------------------------------------------------
// Artifact / Workspace / Storage
// ---------------------------------------------------------------------------

const sha = (suffix: string) => `sha256:${suffix.padEnd(64, "0").slice(0, 64)}`;

export const artifactContents: Record<string, string> = {
	"artifact-01": `# 秋季医療脱毛キャンペーン LP構成案 v2

## 最優先Conversion
Web予約

## 構成
1. ヒーロー（初回価格・期間・予約ボタン）
2. 選ばれる理由（3点）
3. メニューと料金（税込・回数・適用条件）
4. 症例（掲載同意済みのみ）
5. 医師紹介
6. アクセス（青山一丁目 徒歩3分）
7. よくある質問
8. 予約フォーム / LINE

## 医療広告表現の注意
- 体験談は広告利用しない
- 術前術後写真には必要な説明を併記する
- 「必ず」「永久」など断定表現を使用しない
`,
	"artifact-02": `# 競合価格Research Report

| クリニック | 全身脱毛5回 | 初回限定 | 更新日 |
| --- | ---: | ---: | --- |
| A院 | 148,000円 | 29,800円 | 2026-08-18 |
| B院 | 132,000円 | 19,800円 | 2026-08-19 |
| C院 | 168,000円 | なし | 2026-08-15 |

## Evidence
各院の公式サイト料金ページ（取得日 2026-08-19）。
広告表示のみの価格は採用していない。
`,
	"artifact-03": `# Copy Draft v1（差戻し済み）

医療広告表現Gateで「永久に生えてこない」という断定表現が検出された。
Copyを修正して再提出する必要がある。
`,
	"artifact-01-v1": `# 秋季医療脱毛キャンペーン LP構成案 v1

## 最優先Conversion
Web予約

## 構成
1. ヒーロー
2. メニューと料金
3. アクセス
4. 予約フォーム

## 備考
Research結果の反映前。v2で症例・医師紹介・FAQを追加した。
`,
	"artifact-90": "# 他Tenantの成果物\n\nTenant分離検証用。\n",
};

type LegacyArtifact = {
	id: string;
	tenantId: string;
	projectId: string;
	sessionId: string;
	executionId: string;
	ownerUserId: string;
	createdByActorId: string;
	type: string;
	displayName: string;
	version: number;
	mimeType: string;
	size: number;
	checksum: string;
	objectRef: string;
	preview: string;
	createdAt: string;
};

function mockArtifact(input: LegacyArtifact): Artifact {
	return {
		id: input.id,
		tenantId: input.tenantId,
		projectId: input.projectId,
		workspaceId: `workspace-${input.projectId}`,
		ownerUserId: input.ownerUserId,
		generatedInSessionId: input.sessionId,
		generatedByExecutionId: input.executionId,
		generatedByNodeExecutionId: null,
		generatedByActorType: "AGENT",
		generatedByActorId: input.createdByActorId,
		authenticationProfileRef: null,
		name: input.displayName,
		type: input.type,
		mimeType: input.mimeType,
		version: input.version,
		lineageId: input.id.startsWith("artifact-01") ? "artifact-01" : input.id,
		storageRef: input.objectRef,
		sizeBytes: input.size,
		checksum: input.checksum,
		checksumAlgorithm: "sha256",
		status: input.id === "artifact-01-v1" ? "SUPERSEDED" : "COMMITTED",
		retentionUntil: null,
		createdAt: input.createdAt,
	};
}

export const artifacts: Artifact[] = [
	mockArtifact({
		id: "artifact-01",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		sessionId: "session-01",
		executionId: "execution-01",
		ownerUserId: "user-01",
		createdByActorId: "agent-vertical-beauty",
		type: "DOCUMENT",
		displayName: "秋季キャンペーンLP構成案.md",
		version: 2,
		mimeType: "text/markdown",
		size: 612,
		checksum: sha("a1f2"),
		objectRef: "local-object://artifacts/tenant-01/artifact-01/v2.md",
		preview: "# 秋季医療脱毛キャンペーン LP構成案 v2",
		createdAt: t(60),
	}),
	mockArtifact({
		id: "artifact-01-v1",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		sessionId: "session-01",
		executionId: "execution-01",
		ownerUserId: "user-01",
		createdByActorId: "agent-vertical-beauty",
		type: "DOCUMENT",
		displayName: "秋季キャンペーンLP構成案.md",
		version: 1,
		mimeType: "text/markdown",
		size: 480,
		checksum: sha("a1f1"),
		objectRef: "local-object://artifacts/tenant-01/artifact-01/v1.md",
		preview: "# 秋季医療脱毛キャンペーン LP構成案 v1",
		createdAt: t(44),
	}),
	mockArtifact({
		id: "artifact-02",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_SECOND,
		sessionId: "session-02",
		executionId: "execution-02",
		ownerUserId: "user-04",
		createdByActorId: "agent-common-research",
		type: "REPORT",
		displayName: "競合価格Research.md",
		version: 1,
		mimeType: "text/markdown",
		size: 421,
		checksum: sha("b2c3"),
		objectRef: "local-object://artifacts/tenant-01/artifact-02/v1.md",
		preview: "# 競合価格Research Report",
		createdAt: t(40),
	}),
	mockArtifact({
		id: "artifact-03",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		sessionId: "session-03",
		executionId: "execution-03",
		ownerUserId: "user-01",
		createdByActorId: "agent-common-lp",
		type: "DRAFT",
		displayName: "Copy Draft v1.md",
		version: 1,
		mimeType: "text/markdown",
		size: 180,
		checksum: sha("c3d4"),
		objectRef: "local-object://artifacts/tenant-01/artifact-03/v1.md",
		preview: "# Copy Draft v1（差戻し済み）",
		createdAt: t(29),
	}),
	mockArtifact({
		id: "artifact-90",
		tenantId: TENANT_OTHER,
		projectId: PROJECT_OTHER,
		sessionId: "session-90",
		executionId: "execution-90",
		ownerUserId: "user-90",
		createdByActorId: "agent-vertical-restaurant",
		type: "DOCUMENT",
		displayName: "他Tenant成果物.md",
		version: 1,
		mimeType: "text/markdown",
		size: 64,
		checksum: sha("d4e5"),
		objectRef: "local-object://artifacts/tenant-02/artifact-90/v1.md",
		preview: "# 他Tenantの成果物",
		createdAt: t(13),
	}),
];

export const workspaceEntries: WorkspaceEntry[] = [
	{
		id: "ws-01",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		path: "/deliverables",
		kind: "DIRECTORY",
		mimeType: null,
		size: 0,
		artifactId: null,
		updatedAt: t(60),
	},
	{
		id: "ws-02",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		path: "/deliverables/秋季キャンペーンLP構成案.md",
		kind: "FILE",
		mimeType: "text/markdown",
		size: 612,
		artifactId: "artifact-01",
		updatedAt: t(60),
	},
	{
		id: "ws-03",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		path: "/working/notes.md",
		kind: "FILE",
		mimeType: "text/markdown",
		size: 92,
		artifactId: null,
		updatedAt: t(58),
	},
	{
		id: "ws-04",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_SECOND,
		path: "/deliverables/競合価格Research.md",
		kind: "FILE",
		mimeType: "text/markdown",
		size: 421,
		artifactId: "artifact-02",
		updatedAt: t(40),
	},
	{
		id: "ws-90",
		tenantId: TENANT_OTHER,
		projectId: PROJECT_OTHER,
		path: "/deliverables/他Tenant成果物.md",
		kind: "FILE",
		mimeType: "text/markdown",
		size: 64,
		artifactId: "artifact-90",
		updatedAt: t(13),
	},
];

export const storageObjects: StorageObject[] = artifacts.map((a, index) => ({
	id: `storage-${a.id}`,
	tenantId: a.tenantId,
	projectId: a.projectId,
	artifactId: a.id,
	objectRef: a.storageRef,
	mimeType: a.mimeType,
	size: a.sizeBytes,
	checksum: a.checksum,
	storageClass: index === 2 ? "ARCHIVE" : "STANDARD",
	retentionUntil: index === 2 ? "2027-08-20T09:00:00.000Z" : null,
	createdAt: a.createdAt,
}));

export const ownershipTransfers: OwnershipTransferRecord[] = [
	{
		id: "transfer-01",
		resourceType: "ARTIFACT",
		resourceId: "artifact-02",
		fromOwnerUserId: "user-01",
		toOwnerUserId: "user-04",
		fromOwnerLabel: "田中 彩",
		toOwnerLabel: "鈴木 涼",
		reason: "Research担当の引き継ぎ",
		requestedBy: "user-02",
		approvedBy: "user-02",
		transferredAt: t(42),
	},
];

// ---------------------------------------------------------------------------
// Approval
// ---------------------------------------------------------------------------

export const approvals: ApprovalRequest[] = [
	{
		id: "approval-01",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		sessionId: "session-01",
		executionId: "execution-01",
		nodeExecutionId: "node-approval-01",
		gateType: "APPROVAL",
		title: "LP構成案 v2 の承認",
		summary:
			"Guided Interviewの回答とResearch結果に基づく構成案です。医療広告表現Gateは合格しています。公開前の内容確定として承認または差戻しを選択してください。",
		evidence: [
			{ label: "医療広告表現Gate", value: "PASSED (score 0.97 / threshold 0.95)" },
			{ label: "価格・適用条件の正確性", value: "PASSED" },
			{ label: "Mobile予約導線の到達性", value: "PASSED" },
			{ label: "使用Vertical Package", value: "美容クリニック Vertical Package v5" },
		],
		artifactIds: ["artifact-01"],
		indexArtifactId: null,
		siteId: "default",
		status: "PENDING",
		decision: null,
		decidedBy: null,
		decidedAt: null,
		expiresAt: t(60 * 48),
		changeRequestRoute: "copy",
	},
	{
		id: "approval-02",
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		sessionId: "session-03",
		executionId: "execution-03",
		nodeExecutionId: "node-approval-03",
		gateType: "QUALITY",
		title: "Copy Draft v1 の品質判定",
		summary: "医療広告表現Gateで断定表現が検出されたため差戻しました。",
		evidence: [{ label: "医療広告表現Gate", value: "FAILED — 断定表現「永久に生えてこない」" }],
		artifactIds: ["artifact-03"],
		indexArtifactId: null,
		siteId: "default",
		status: "FAILED",
		decision: "CHANGES_REQUESTED",
		decidedBy: "user-01",
		decidedAt: t(30),
		expiresAt: null,
		changeRequestRoute: "copy",
	},
];

// ---------------------------------------------------------------------------
// Provider / Credential
// ---------------------------------------------------------------------------

export const providers: ProviderView[] = [
	{ id: "provider-fake", displayName: "Local Fake Provider", status: "ACTIVE" },
	{ id: "provider-anthropic", displayName: "Anthropic", status: "ACTIVE" },
	{ id: "provider-openai", displayName: "OpenAI", status: "ACTIVE" },
	{ id: "provider-google", displayName: "Google", status: "MAINTENANCE" },
];

export const personalCredentials: PersonalCredential[] = [
	{
		id: "cred-01",
		userId: "user-01",
		providerId: "provider-anthropic",
		providerLabel: "Anthropic",
		displayName: "個人Claude Subscription",
		authMethod: "SUBSCRIPTION_LOGIN",
		secretRef: "vault:user-01/anthropic/session",
		maskedHint: "••••あなたの登録済みSession",
		status: "ACTIVE",
		lastVerifiedAt: t(50),
		lastTestResult: "SUCCESS",
		lastTestReason: null,
		createdAt: t(48),
	},
	{
		id: "cred-02",
		userId: "user-01",
		providerId: "provider-openai",
		providerLabel: "OpenAI",
		displayName: "個人OpenAI API Key",
		authMethod: "API_KEY",
		secretRef: "vault:user-01/openai/api-key",
		maskedHint: "sk-••••••••3f2a",
		status: "REAUTH_REQUIRED",
		lastVerifiedAt: t(20),
		lastTestResult: "FAILED",
		lastTestReason: "AUTH_PROFILE_EXPIRED",
		createdAt: t(18),
	},
];

export const poolAssignments: PoolAssignment[] = [
	{
		id: "assign-01",
		poolId: "pool-fake",
		poolAlias: "Standard Assist",
		providerId: "provider-fake",
		subjectType: "TENANT",
		subjectId: TENANT_MAIN,
		subjectLabel: "Tenant全体",
		effect: "ALLOW",
		allowedPurposes: ["ASSIST", "INTERVIEW", "DOCUMENT"],
		validFrom: t(0),
		validUntil: null,
	},
	{
		id: "assign-02",
		poolId: "pool-claude-coding",
		poolAlias: "Claude Coding Standard",
		providerId: "provider-anthropic",
		subjectType: "USER",
		subjectId: "user-01",
		subjectLabel: "田中 彩",
		effect: "ALLOW",
		allowedPurposes: ["CODING", "DESIGN", "DOCUMENT"],
		validFrom: t(0),
		validUntil: null,
	},
	{
		id: "assign-03",
		poolId: "pool-claude-coding",
		poolAlias: "Claude Coding Standard",
		providerId: "provider-anthropic",
		subjectType: "USER",
		subjectId: "user-04",
		subjectLabel: "鈴木 涼",
		effect: "DENY",
		allowedPurposes: [],
		validFrom: t(0),
		validUntil: null,
	},
];

export const byoPolicy: ByoPolicy = {
	tenantId: TENANT_MAIN,
	byokAllowed: true,
	byosAllowed: true,
	platformByokAllowed: true,
	platformByosAllowed: true,
	allowedProviderIds: ["provider-anthropic", "provider-openai"],
	note: "Google ProviderはProvider Policyが UNVERIFIED のため個人認証を許可できない。",
};

export const providerUsageRules: ProviderUsageRule[] = [
	{
		id: "rule-01",
		providerId: "provider-anthropic",
		taskType: "CODING",
		routeConstraintMode: "PREFERRED",
		priority: 10,
		lockedByPlatform: false,
	},
	{
		id: "rule-02",
		providerId: "provider-openai",
		taskType: "RESEARCH",
		routeConstraintMode: "PREFERRED",
		priority: 10,
		lockedByPlatform: false,
	},
	{
		id: "rule-03",
		providerId: "provider-fake",
		taskType: "DOCUMENT",
		routeConstraintMode: "REQUIRED",
		priority: 0,
		lockedByPlatform: true,
	},
];

// ---------------------------------------------------------------------------
// Tenant Admin
// ---------------------------------------------------------------------------

export const members: TenantMember[] = [
	{
		id: "member-01",
		tenantId: TENANT_MAIN,
		email: "user@example.test",
		name: "田中 彩",
		role: "tenant_member",
		status: "ACTIVE",
		lastActiveAt: t(64),
		joinedAt: t(0),
	},
	{
		id: "member-02",
		tenantId: TENANT_MAIN,
		email: "admin@example.test",
		name: "佐藤 健",
		role: "tenant_admin",
		status: "ACTIVE",
		lastActiveAt: t(62),
		joinedAt: t(0),
	},
	{
		id: "member-04",
		tenantId: TENANT_MAIN,
		email: "member2@example.test",
		name: "鈴木 涼",
		role: "tenant_member",
		status: "ACTIVE",
		lastActiveAt: t(41),
		joinedAt: t(0),
	},
	{
		id: "member-05",
		tenantId: TENANT_MAIN,
		email: "invited@example.test",
		name: "招待済みUser",
		role: "tenant_member",
		status: "INVITED",
		lastActiveAt: null,
		joinedAt: t(55),
	},
	{
		id: "member-90",
		tenantId: TENANT_OTHER,
		email: "other@other.test",
		name: "別Tenant User",
		role: "tenant_member",
		status: "ACTIVE",
		lastActiveAt: t(15),
		joinedAt: t(0),
	},
];

export const invites: TenantInvite[] = [
	{
		id: "invite-01",
		tenantId: TENANT_MAIN,
		email: "invited@example.test",
		role: "tenant_member",
		status: "PENDING",
		invitedBy: "user-02",
		expiresAt: t(60 * 24 * 7),
		createdAt: t(55),
		token: "invite-token-demo-01",
	},
];

export const roleDefinitions: RoleDefinition[] = [
	{
		role: "tenant_member",
		displayName: "Tenant User",
		description: "Agentの利用、Session作成、自分の成果物の閲覧・Downloadを行う。",
		permissions: ["project.read", "session.create", "artifact.download", "credential.byok"],
		lockedPermissions: [],
	},
	{
		role: "tenant_admin",
		displayName: "Tenant Admin",
		description: "Member、利用可能Agent、認証、予算、Auditを管理する。",
		permissions: [
			"project.read",
			"project.write",
			"session.create",
			"session.read.all",
			"artifact.download",
			"artifact.transfer",
			"credential.byok",
			"credential.byos",
			"admin.member",
			"admin.budget",
			"admin.audit",
			"admin.integration",
		],
		lockedPermissions: ["admin.audit"],
	},
	{
		role: "platform_admin",
		displayName: "Agentry System Admin",
		description: "Agentry製品のTenant、公開Catalog、Plan、CMSを管理する。基盤Secretは扱わない。",
		permissions: ["project.read", "session.read.all", "admin.audit"],
		lockedPermissions: ["admin.audit"],
	},
	{
		role: "platform_viewer",
		displayName: "Product Viewer",
		description: "製品管理の参照のみ。",
		permissions: ["project.read"],
		lockedPermissions: [],
	},
];

export const catalogEntitlements: TenantCatalogEntitlement[] = [
	{
		ucsId: REPRESENTATIVE_UCS,
		enabled: true,
		restrictedReason: null,
		allowedRoles: ["tenant_member", "tenant_admin"],
	},
	{
		ucsId: "UCS-WEB-001-02-v1",
		enabled: true,
		restrictedReason: null,
		allowedRoles: ["tenant_member", "tenant_admin"],
	},
	{
		ucsId: "UCS-CMN-002-01-v1",
		enabled: true,
		restrictedReason: null,
		allowedRoles: ["tenant_member", "tenant_admin"],
	},
	{
		ucsId: "UCS-WEB-020-01-v1",
		enabled: false,
		restrictedReason: "Tenant Adminがこの業種UCSを有効化していません。",
		allowedRoles: [],
	},
	{
		ucsId: "UCS-MKT-001-01-v1",
		enabled: false,
		restrictedReason: "公開前のDraft UCSです。",
		allowedRoles: [],
	},
];

export const budgets: Budget[] = [
	{
		id: "budget-01",
		tenantId: TENANT_MAIN,
		scope: "TENANT",
		scopeId: TENANT_MAIN,
		scopeLabel: "Tenant全体",
		currency: "JPY",
		limitAmount: 300000,
		consumedAmount: 84250,
		reservedAmount: 12000,
		period: "2026-08",
		onExceeded: "REQUIRE_APPROVAL",
	},
	{
		id: "budget-02",
		tenantId: TENANT_MAIN,
		scope: "PROJECT",
		scopeId: PROJECT_MAIN,
		scopeLabel: "青山院 秋季キャンペーン",
		currency: "JPY",
		limitAmount: 120000,
		consumedAmount: 61200,
		reservedAmount: 8000,
		period: "2026-08",
		onExceeded: "BLOCK",
	},
	{
		id: "budget-03",
		tenantId: TENANT_MAIN,
		scope: "USER",
		scopeId: "user-01",
		scopeLabel: "田中 彩",
		currency: "JPY",
		limitAmount: 50000,
		consumedAmount: 47800,
		reservedAmount: 0,
		period: "2026-08",
		onExceeded: "WARN",
	},
];

export const integrations: Integration[] = [
	{
		id: "integ-01",
		tenantId: TENANT_MAIN,
		kind: "SLACK",
		displayName: "承認通知 Slack",
		status: "CONNECTED",
		secretRef: "vault:tenant-01/slack/webhook",
		lastSyncedAt: t(60),
	},
	{
		id: "integ-02",
		tenantId: TENANT_MAIN,
		kind: "WEBHOOK",
		displayName: "社内CMS Webhook",
		status: "ERROR",
		secretRef: "vault:tenant-01/cms/webhook",
		lastSyncedAt: t(20),
	},
	{
		id: "integ-03",
		tenantId: TENANT_MAIN,
		kind: "GOOGLE_DRIVE",
		displayName: "成果物バックアップ",
		status: "DISCONNECTED",
		secretRef: null,
		lastSyncedAt: null,
	},
];

export const tenantSettings: TenantSettings = {
	tenantId: TENANT_MAIN,
	displayName: "Aoyama Beauty Clinic Group",
	slug: "aoyama-beauty",
	locale: "ja",
	defaultProjectId: PROJECT_MAIN,
	dataResidency: "JP",
	requireApprovalForPublish: true,
	sessionRetentionDays: 365,
};

// ---------------------------------------------------------------------------
// System Admin
// ---------------------------------------------------------------------------

export const tenantSummaries: TenantSummary[] = [
	{
		id: TENANT_MAIN,
		name: "Aoyama Beauty Clinic Group",
		slug: "aoyama-beauty",
		status: "ACTIVE",
		planId: "plan-pro",
		memberCount: 4,
		monthlyCost: 84250,
		createdAt: t(0),
	},
	{
		id: TENANT_OTHER,
		name: "Sakura Dining Holdings",
		slug: "sakura-dining",
		status: "ACTIVE",
		planId: "plan-basic",
		memberCount: 1,
		monthlyCost: 12400,
		createdAt: t(0),
	},
];

export const plans: Plan[] = [
	{
		id: "plan-trial",
		displayName: "Trial",
		tier: "TRIAL",
		monthlyPrice: 0,
		currency: "JPY",
		runtimeHours: 30,
		seats: 3,
		entitlements: ["UCS-CMN-002-01-v1"],
		status: "ACTIVE",
	},
	{
		id: "plan-basic",
		displayName: "Basic",
		tier: "BASIC",
		monthlyPrice: 49800,
		currency: "JPY",
		runtimeHours: 80,
		seats: 10,
		entitlements: ["UCS-CMN-002-01-v1", "UCS-WEB-001-02-v1"],
		status: "ACTIVE",
	},
	{
		id: "plan-pro",
		displayName: "Pro",
		tier: "PRO",
		monthlyPrice: 148000,
		currency: "JPY",
		runtimeHours: 160,
		seats: 30,
		entitlements: ["UCS-CMN-002-01-v1", "UCS-WEB-001-02-v1", REPRESENTATIVE_UCS],
		status: "ACTIVE",
	},
	{
		id: "plan-enterprise",
		displayName: "Enterprise",
		tier: "ENTERPRISE",
		monthlyPrice: 0,
		currency: "JPY",
		runtimeHours: 720,
		seats: 300,
		entitlements: ["*"],
		status: "ACTIVE",
	},
];

export const publications: CatalogPublication[] = ucsList.map((u) => ({
	ucsId: u.id,
	displayName: u.displayName,
	verticalKey: u.verticalKey,
	status: u.status,
	publishedAt: u.status === "PUBLISHED" ? t(0) : null,
	restrictedToTenantIds: u.id === "UCS-WEB-020-01-v1" ? [TENANT_OTHER] : [],
}));

export const notices: Notice[] = [
	{
		id: "notice-01",
		title: "美容クリニックVertical Package v5 を公開しました",
		body: "医療広告表現Gateの判定基準を更新し、症例写真の掲載同意Gateを追加しました。",
		category: "RELEASE",
		status: "PUBLISHED",
		publishedAt: t(2),
		audience: "ALL",
	},
	{
		id: "notice-02",
		title: "定期メンテナンス予定",
		body: "2026-09-01 02:00-04:00 JST に基盤メンテナンスを実施します。",
		category: "MAINTENANCE",
		status: "DRAFT",
		publishedAt: null,
		audience: "TENANT_ADMIN",
	},
];

export const contactMessages: ContactMessage[] = [
	{
		id: "contact-01",
		name: "山田 太郎",
		email: "yamada@example.test",
		company: "株式会社サンプル",
		subject: "不動産Verticalの対応予定について",
		body: "賃貸仲介向けのVerticalは提供予定がありますか。",
		status: "NEW",
		createdAt: t(58),
	},
	{
		id: "contact-02",
		name: "高橋 花子",
		email: "takahashi@example.test",
		company: "Sakura Dining",
		subject: "Enterprise Planの見積",
		body: "10店舗分の見積をお願いします。",
		status: "IN_PROGRESS",
		createdAt: t(35),
	},
];

export const productSettings: ProductSettings = {
	productName: "Agentry",
	supportEmail: "support@agentry.test",
	signupEnabled: true,
	demoEnabled: true,
	defaultPlanId: "plan-trial",
	maintenanceMode: false,
};

// ---------------------------------------------------------------------------
// Cost / Audit / Notification
// ---------------------------------------------------------------------------

export const costEvents: CostEventView[] = [
	{
		id: "cost-01",
		sessionId: "session-01",
		executionId: "execution-01",
		providerId: "provider-anthropic",
		modelId: "model-claude",
		meteringMode: "ACTUAL_MONEY",
		inputTokens: 18420,
		outputTokens: 6210,
		amount: 412,
		currency: "JPY",
		createdAt: t(30),
	},
	{
		id: "cost-02",
		sessionId: "session-01",
		executionId: "execution-01",
		providerId: "provider-anthropic",
		modelId: "model-claude",
		meteringMode: "ESTIMATED_OPPORTUNITY_COST",
		inputTokens: 9800,
		outputTokens: 3100,
		amount: 0,
		currency: "JPY",
		createdAt: t(48),
	},
	{
		id: "cost-03",
		sessionId: "session-02",
		executionId: "execution-02",
		providerId: "provider-openai",
		modelId: "model-gpt",
		meteringMode: "ACTUAL_MONEY",
		inputTokens: 12200,
		outputTokens: 4400,
		amount: 288,
		currency: "JPY",
		createdAt: t(38),
	},
	{
		id: "cost-04",
		sessionId: "session-03",
		executionId: "execution-03",
		providerId: "provider-openai",
		modelId: null,
		meteringMode: "UNAVAILABLE",
		inputTokens: 0,
		outputTokens: 0,
		amount: null,
		currency: "JPY",
		createdAt: t(31),
	},
];

export const auditEvents: AuditEventView[] = [
	{
		id: "audit-01",
		actorId: "user-02",
		action: "byo_policy.update",
		resourceType: "TENANT",
		resourceId: TENANT_MAIN,
		reason: "個人Subscription利用を許可",
		traceId: "trace-a1",
		createdAt: t(46),
	},
	{
		id: "audit-02",
		actorId: "user-01",
		action: "credential.create",
		resourceType: "PERSONAL_CREDENTIAL",
		resourceId: "cred-01",
		reason: "個人Claude Subscriptionを登録",
		traceId: "trace-a2",
		createdAt: t(48),
	},
	{
		id: "audit-03",
		actorId: "user-01",
		action: "approval.changes_requested",
		resourceType: "APPROVAL",
		resourceId: "approval-02",
		reason: "医療広告表現Gate不合格のため差戻し",
		traceId: "trace-a3",
		createdAt: t(30),
	},
	{
		id: "audit-04",
		actorId: "user-02",
		action: "ownership.transfer",
		resourceType: "ARTIFACT",
		resourceId: "artifact-02",
		reason: "Research担当の引き継ぎ",
		traceId: "trace-a4",
		createdAt: t(42),
	},
	{
		id: "audit-05",
		actorId: "user-02",
		action: "pool_assignment.deny",
		resourceType: "SYSTEM_AUTH_POOL",
		resourceId: "pool-claude-coding",
		reason: "鈴木 涼のCoding用途利用を停止",
		traceId: "trace-a5",
		createdAt: t(44),
	},
];

export const notifications: NotificationItem[] = [
	{
		id: "notif-01",
		userId: "user-01",
		kind: "APPROVAL_REQUESTED",
		title: "LP構成案 v2 の承認待ち",
		body: "秋季キャンペーンLP制作Sessionで承認が必要です。",
		read: false,
		link: "/app/approvals/approval-01",
		createdAt: t(60),
	},
	{
		id: "notif-02",
		userId: "user-01",
		kind: "CREDENTIAL_REFRESH",
		title: "個人OpenAI API Keyの再認証が必要です",
		body: "接続Testが AUTH_PROFILE_EXPIRED で失敗しました。設定画面から再登録してください。",
		read: false,
		link: "/app/settings/credentials",
		createdAt: t(52),
	},
	{
		id: "notif-03",
		userId: "user-01",
		kind: "EXECUTION_FINISHED",
		title: "競合価格Researchが完了しました",
		body: "成果物 競合価格Research.md を確認できます。",
		read: true,
		link: "/app/artifacts/artifact-02",
		createdAt: t(41),
	},
];
